import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField
} from "@mui/material";

import { AlertNotification } from "../utils/Notifications";
import useAuth from "../../hooks/useAuth";

import { updateUser } from "../../functions/server-requests/user";

const EditProfileDialog = ({
    openEditProfileDialog,
    setOpenEditProfileDialog,
    userDetails,
    setUserDetails,
    setOpenSnackbar,
    setSnackbarMessage
}) => {
    const { auth, setAuth } = useAuth();
    const [openAlert, setOpenAlert] = useState(false);
    const [message, setMessage] = useState("");
    const [isSubmitting, setSubmitting] = useState(false);

    const validationSchema = yup.object({
        name: yup
            .string()
            .required('Required')
            .min(3, 'Must be at least 3 characters')
            .max(64, 'Must be at most 64 characters'),
        username: yup
            .string()
            .required('Required')
            .min(3, 'Must be at least 3 characters')
            .max(20, 'Must be at most 20 characters')
            .matches(
                /^[a-z0-9_]*$/,
                'Only small alphabates, digits and underscore is allowed'
            ),
        email: yup
            .string()
            .required('Required')
            .email('Invalid email')
            .max(128, 'Must be at most 128 characters'),
        password: yup
            .string()
            .min(8, 'Must be at least 8 characters')
            .max(20, 'Must be at most 20 characters')
    });

    const handleSubmit = async (values) => {
        setSubmitting(true);
        setOpenAlert(false);

        const updateUserRes = await updateUser({
            loggedInUserId: auth.userId,
            userId: userDetails._id,
            ...values
        });

        if (updateUserRes.status === 201) {
            setUserDetails({
                ...userDetails,
                name: values.name,
                username: values.username,
                email: values.email
            });

            setAuth({
                ...auth,
                name: values.name
            });

            setSnackbarMessage(updateUserRes.data.message);
            setOpenSnackbar(true);
            setOpenEditProfileDialog(false);
        }
        else {
            setMessage(updateUserRes.data.message);
            setOpenAlert(true);
        }

        setSubmitting(false);
    }

    const styles = {
        marginBottom: "24px"
    };

    const formik = useFormik({
        initialValues: {
            name: userDetails.name,
            username: userDetails.username,
            email: userDetails.email,
            password: ""
        },
        validationSchema,
        onSubmit: async (values) => {
            handleSubmit(values);
        }
    });

    return (
        <Dialog
            style={{
                margin: "auto",
                width: "100%",
                maxWidth: "12cm"
            }}
            open={openEditProfileDialog}
            onClose={() => setOpenEditProfileDialog(false)}
        >
            <DialogTitle
                style={{ textAlign: "center" }}
            >
                <span className="title m-title">
                    Update Profile
                </span>
            </DialogTitle>

            <div
                style={{
                    margin: "auto",
                    width: "calc(100% - 48px)"
                }}
            >
                <AlertNotification
                    openAlert={openAlert}
                    setOpenAlert={setOpenAlert}
                    severity="error"
                    styles={{ marginBottom: "0" }}
                    message={message}
                />
            </div>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <TextField
                        fullWidth
                        required
                        style={styles}
                        type="text"
                        name="name"
                        label="Name"
                        placeholder="Enter your name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        disabled={isSubmitting}
                    />

                    <TextField
                        fullWidth
                        required
                        style={styles}
                        type="text"
                        name="username"
                        label="Username"
                        placeholder="Enter your username"
                        value={formik.values.username}
                        onChange={formik.handleChange}
                        error={formik.touched.username && Boolean(formik.errors.username)}
                        helperText={formik.touched.username && formik.errors.username}
                        disabled={isSubmitting}
                    />

                    <TextField
                        fullWidth
                        required
                        style={styles}
                        type="email"
                        name="email"
                        label="Email Address"
                        placeholder="Enter your email address"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        disabled={isSubmitting}
                    />

                    <TextField
                        fullWidth
                        style={styles}
                        type="password"
                        name="password"
                        label="Password"
                        placeholder="Enter your password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        disabled={isSubmitting}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpenEditProfileDialog(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {!isSubmitting
                            ? "Update"
                            : "Updating ..."
                        }
                    </Button>
                </DialogActions>
            </form>
        </Dialog >
    );
}

export default EditProfileDialog;